import { z } from 'zod'
import { ticketFeeModeValues } from '../ticket-fees'
import {
  billingEntityInputSchema,
  billingEntitySchema,
  isVatId,
  normalizeVatId,
} from './billing-entity'
import { subscriptionTierSchema } from './subscription'

export const ticketFeeModeSchema = z.enum(ticketFeeModeValues)

export const ticketOrderStatusValues = ['pending', 'paid', 'expired', 'refunded', 'free'] as const
export type TicketOrderStatus = (typeof ticketOrderStatusValues)[number]

export const ticketStatusValues = ['active', 'void', 'refunded'] as const
export type TicketStatus = (typeof ticketStatusValues)[number]

export const TICKET_ORDER_MAX_QUANTITY = 10

/** The VAT on ticket invoices: `exempt` for sellers who charge none, like small businesses. */
export const ticketVatTreatmentValues = ['exempt', 'rate_10', 'rate_13', 'rate_20'] as const
export type TicketVatTreatment = (typeof ticketVatTreatmentValues)[number]
export const ticketVatTreatmentSchema = z.enum(ticketVatTreatmentValues)

/** GET/PUT /api/businesses/{businessId}/tickets/settings: how the business sells tickets. */
export const ticketSettingsSchema = z.object({
  feeMode: ticketFeeModeSchema,
  tier: subscriptionTierSchema,
  fee: z.object({ basisPoints: z.number().int(), fixedCents: z.number().int() }),
  connect: z.object({
    /** False while Pulsify cannot take payouts yet; paid tickets cannot be set up then. */
    available: z.boolean(),
    accountId: z.string().nullable(),
    onboardingComplete: z.boolean(),
    payoutsEnabled: z.boolean(),
    /** The profile asked about pays into its own account, not its organization's. */
    own: z.boolean(),
  }),
  /** Sales limit until the business's first ticketed event took place; null once lifted. */
  firstSalesCap: z.object({ limitCents: z.number().int(), usedCents: z.number().int() }).nullable(),
  /** Who sells the tickets on their invoices and at which VAT. */
  invoice: z.object({
    vatTreatment: ticketVatTreatmentSchema.nullable(),
    issuer: billingEntitySchema.nullable(),
    /** The profile asked about sells as its own company, not as its organization. */
    ownIssuer: z.boolean(),
    /** Name, address and VAT are all there, so buyers get invoices. */
    ready: z.boolean(),
  }),
})
export type TicketSettings = z.infer<typeof ticketSettingsSchema>

/** Which profile's setup to read or change; none means the organization's. */
export const ticketSettingsQuerySchema = z.object({
  hostProfileId: z.string().uuid().optional(),
})

export const updateTicketSettingsRequestSchema = z.object({
  feeMode: ticketFeeModeSchema.optional(),
  vatTreatment: ticketVatTreatmentSchema.nullable().optional(),
  hostProfileId: z.string().uuid().optional(),
})

/** PUT /api/businesses/{businessId}/tickets/invoice-issuer */
export const updateTicketInvoiceIssuerRequestSchema = z.object({
  details: billingEntityInputSchema,
  hostProfileId: z.string().uuid().optional(),
  /** Sell this profile's tickets as a company of its own instead of its organization. */
  own: z.boolean().optional(),
})

export const connectOnboardingRequestSchema = z.object({
  returnUrl: z.string().url(),
  refreshUrl: z.string().url(),
  /** Sets up the profile's own payout account instead of the organization's. */
  hostProfileId: z.string().uuid().optional(),
})

export const connectOnboardingResponseSchema = z.object({ url: z.string() })

export const ticketTypeInputSchema = z.object({
  name: z.string().trim().min(1).max(80),
  priceCents: z.number().int().min(0).max(1_000_000),
  quantity: z.number().int().positive().max(100_000).nullable().optional(),
  isStudent: z.boolean().optional(),
  /** Paid at the door on top of a free ticket; ignored for paid types. */
  doorPriceCents: z.number().int().min(0).max(1_000_000).nullable().optional(),
  /** Nobody can get this type from this moment on; null sells it until the event ends. */
  salesEndAt: z.iso.datetime().nullable().optional(),
  active: z.boolean().optional(),
  sort: z.number().int().min(0).max(1000).optional(),
})

export const updateTicketTypeRequestSchema = ticketTypeInputSchema.partial()

/** A ticket type as the organizer manages it. */
export const ticketTypeSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  name: z.string(),
  priceCents: z.number().int(),
  quantity: z.number().int().nullable(),
  soldCount: z.number().int(),
  isStudent: z.boolean(),
  /** Paid at the door on top of a free ticket; null when nothing is due there. */
  doorPriceCents: z.number().int().nullable(),
  salesEndAt: z.string().nullable(),
  active: z.boolean(),
  sort: z.number().int(),
})
export type TicketType = z.infer<typeof ticketTypeSchema>

export const ticketTypesResponseSchema = z.object({
  types: z.array(ticketTypeSchema),
  /** This event's VAT when it differs from the organizer's usual one. */
  vatTreatment: ticketVatTreatmentSchema.nullable(),
})

export const eventTicketVatRequestSchema = z.object({
  vatTreatment: ticketVatTreatmentSchema.nullable(),
})

/** A ticket type as a buyer sees it, with the fee they pay on top. */
export const publicTicketTypeSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  priceCents: z.number().int(),
  feeCents: z.number().int(),
  buyerPriceCents: z.number().int(),
  isStudent: z.boolean(),
  /** Paid at the door on top of a free ticket; null when nothing is due there. */
  doorPriceCents: z.number().int().nullable(),
  /** Seats left; null for no limit. */
  remaining: z.number().int().nullable(),
  soldOut: z.boolean(),
  /** Nobody can get this type from this moment on; null sells it until the event ends. */
  salesEndAt: z.string().nullable().optional(),
})

/** GET /api/tickets/events/{eventId}/types */
export const eventTicketTypesResponseSchema = z.object({
  eventId: z.string().uuid(),
  currency: z.string(),
  feeMode: ticketFeeModeSchema,
  /** Paid types can be bought; false while the organizer's payments are not set up. */
  paidSalesOpen: z.boolean(),
  /** Types past their sale end are left out; this says some were there, so sales have closed. */
  salesEnded: z.boolean().optional(),
  types: z.array(publicTicketTypeSchema),
})
export type EventTicketTypesResponse = z.infer<typeof eventTicketTypesResponseSchema>

export const createTicketOrderRequestSchema = z.object({
  ticketTypeId: z.string().uuid(),
  quantity: z.number().int().min(1).max(TICKET_ORDER_MAX_QUANTITY).default(1),
  /** Where Stripe sends the buyer after paying; required for paid tickets. */
  successUrl: z.string().url().optional(),
  cancelUrl: z.string().url().optional(),
})

export const ticketOrderSchema = z.object({
  id: z.string().uuid(),
  reference: z.string(),
  status: z.enum(ticketOrderStatusValues),
  eventId: z.string().uuid(),
  ticketTypeId: z.string().uuid(),
  quantity: z.number().int(),
  unitPriceCents: z.number().int(),
  feePerTicketCents: z.number().int(),
  subtotalCents: z.number().int(),
  feeCents: z.number().int(),
  totalCents: z.number().int(),
  feeMode: ticketFeeModeSchema,
  currency: z.string(),
  createdAt: z.string(),
  paidAt: z.string().nullable(),
})
export type TicketOrder = z.infer<typeof ticketOrderSchema>

/** A ticket as its holder sees it. `qrPayload` is null unless the ticket is valid. */
export const ticketSchema = z.object({
  id: z.string().uuid(),
  reference: z.string(),
  status: z.enum(ticketStatusValues),
  orderId: z.string().uuid(),
  eventId: z.string().uuid(),
  eventTitle: z.string(),
  /** The event's header picture, the face of the ticket; null without one. */
  eventImageUrl: z.string().nullable(),
  /** The holder's name, printed big on the face of the ticket. */
  holderName: z.string().nullable(),
  /** The holder bought it, so the order's invoice is theirs. */
  heldByBuyer: z.boolean().optional(),
  eventStartTime: z.string(),
  eventEndTime: z.string(),
  locationName: z.string().nullable(),
  ticketTypeName: z.string(),
  isStudent: z.boolean(),
  /** Printed on the ticket. */
  faceValueCents: z.number().int(),
  /** Still to pay at the door on a free ticket; null when nothing is due there. */
  doorPriceCents: z.number().int().nullable(),
  currency: z.string(),
  checkedInAt: z.string().nullable(),
  qrPayload: z.string().nullable(),
})
export type Ticket = z.infer<typeof ticketSchema>

const invoiceText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((value) => value || null)

/** Who a buyer's invoice is made out to; everything is optional for a private purchase. */
export const ticketInvoiceRecipientSchema = z.object({
  name: invoiceText(120),
  company: invoiceText(255),
  street: invoiceText(200),
  postalCode: invoiceText(20),
  city: invoiceText(100),
  country: invoiceText(2).transform((value) => value?.toUpperCase() ?? null),
  vatId: z.string().nullish().transform(normalizeVatId).refine(isVatId, 'Invalid VAT number'),
})

export const ticketInvoiceReasonValues = ['not_paid', 'organizer_details_missing'] as const

/** GET/PUT /api/tickets/orders/{orderId}/invoice */
export const ticketInvoiceStateSchema = z.object({
  available: z.boolean(),
  /** Why there is no invoice yet; null when there is one. */
  reason: z.enum(ticketInvoiceReasonValues).nullable(),
  number: z.string().nullable(),
  recipient: z
    .object({
      name: z.string().nullable(),
      company: z.string().nullable(),
      street: z.string().nullable(),
      postalCode: z.string().nullable(),
      city: z.string().nullable(),
      country: z.string().nullable(),
      vatId: z.string().nullable(),
    })
    .nullable(),
  /** The order was refunded, so the invoice is cancelled. */
  canceled: z.boolean(),
})
export type TicketInvoiceState = z.infer<typeof ticketInvoiceStateSchema>

export const ticketDocumentLinkResponseSchema = z.object({ url: z.string() })

export const ticketInvoiceMailResponseSchema = z.object({ sent: z.boolean() })

export const createTicketOrderResponseSchema = z.object({
  order: ticketOrderSchema,
  tickets: z.array(ticketSchema),
  /** Stripe Checkout to send the buyer to; null for free tickets. */
  checkoutUrl: z.string().nullable(),
})

export const ticketOrderResponseSchema = z.object({
  order: ticketOrderSchema,
  tickets: z.array(ticketSchema),
})

export const myTicketsResponseSchema = z.object({ tickets: z.array(ticketSchema) })

export const ticketResponseSchema = z.object({ ticket: ticketSchema })

/**
 * Where a checkout stands once its payment window was closed: `released` nothing was paid and
 * the held tickets are free again, `paid` the tickets are issued, `processing` Stripe took the
 * payment and the tickets follow.
 */
export const ticketCheckoutStateValues = ['released', 'paid', 'processing'] as const
export type TicketCheckoutState = (typeof ticketCheckoutStateValues)[number]

/** POST /api/tickets/orders/{orderId}/cancel */
export const cancelTicketCheckoutResponseSchema = z.object({
  order: ticketOrderSchema,
  state: z.enum(ticketCheckoutStateValues),
})
export type CancelTicketCheckoutResponse = z.infer<typeof cancelTicketCheckoutResponseSchema>

/** POST /api/tickets/{ticketId}/transfer: passes a valid ticket on to an accepted friend. */
export const ticketTransferRequestSchema = z.object({ toProfileId: z.string().uuid() })

export const ticketTransferResponseSchema = z.object({
  ticketId: z.string().uuid(),
  eventId: z.string().uuid(),
  /** The friend's name, now printed on the ticket. */
  holderName: z.string().nullable(),
  transferredAt: z.string(),
})
export type TicketTransferResponse = z.infer<typeof ticketTransferResponseSchema>

export const ticketScanRequestSchema = z.object({
  eventId: z.string().uuid(),
  payload: z.string().trim().min(1).max(512),
})

export const ticketScanResponseSchema = z.object({
  ticketId: z.string().uuid(),
  reference: z.string(),
  holderFirstName: z.string(),
  ticketTypeName: z.string(),
  /** The door checks a student ID. */
  isStudent: z.boolean(),
  faceValueCents: z.number().int(),
  /** To collect at the door on a free ticket; null when nothing is due. */
  doorPriceCents: z.number().int().nullable(),
  checkedInAt: z.string(),
  /** Whether the holder was checked into the event's Live Room too. */
  liveRoom: z.enum(['checked_in', 'skipped']),
})
export type TicketScanResponse = z.infer<typeof ticketScanResponseSchema>

/** One ticket in the organizer's attendee list. */
export const ticketAttendeeSchema = z.object({
  id: z.string().uuid(),
  reference: z.string(),
  status: z.enum(ticketStatusValues),
  holderName: z.string().nullable(),
  ticketTypeName: z.string(),
  isStudent: z.boolean(),
  faceValueCents: z.number().int(),
  checkedInAt: z.string().nullable(),
})

export const ticketOrderWithAttendeesSchema = ticketOrderSchema.extend({
  buyerName: z.string().nullable(),
  applicationFeeCents: z.number().int(),
  tickets: z.array(ticketAttendeeSchema),
})

/** GET /api/businesses/{businessId}/tickets/events/{eventId}/orders */
export const eventTicketOrdersResponseSchema = z.object({
  orders: z.array(ticketOrderWithAttendeesSchema),
  summary: z.object({
    ticketsIssued: z.number().int(),
    checkedIn: z.number().int(),
    grossCents: z.number().int(),
    organizerNetCents: z.number().int(),
  }),
})

export const ticketOrderListStatusValues = ['paid', 'free', 'refunded'] as const
export type TicketOrderListStatus = (typeof ticketOrderListStatusValues)[number]

/** `checked_in`: someone of the order is in; `missing`: a valid ticket of it is not in yet. */
export const ticketOrderCheckInValues = ['checked_in', 'missing'] as const
export type TicketOrderCheckIn = (typeof ticketOrderCheckInValues)[number]

/** `newest` by order time, `name` by buyer A to Z, `amount` highest first. */
export const ticketOrderSortValues = ['newest', 'name', 'amount'] as const
export type TicketOrderSort = (typeof ticketOrderSortValues)[number]

const TICKET_ORDERS_PAGE_SIZE = 50
const TICKET_ORDERS_PAGE_MAX = 100

/** Narrows the organizer's order list; the CSV export takes the same filters. */
export const ticketOrdersFilterSchema = z.object({
  /** Part of the buyer's name or email, the order reference or a ticket reference. */
  search: z.string().trim().max(100).optional(),
  typeId: z.string().uuid().optional(),
  status: z.enum(ticketOrderListStatusValues).optional(),
  checkIn: z.enum(ticketOrderCheckInValues).optional(),
  sort: z.enum(ticketOrderSortValues).default('newest'),
})
export type TicketOrdersFilter = z.infer<typeof ticketOrdersFilterSchema>

export const ticketOrdersPageQuerySchema = ticketOrdersFilterSchema.extend({
  page: z.coerce.number().int().min(1).max(100_000).default(1),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(TICKET_ORDERS_PAGE_MAX)
    .default(TICKET_ORDERS_PAGE_SIZE),
})

export const ticketOrderListItemSchema = ticketOrderWithAttendeesSchema.extend({
  ticketTypeName: z.string(),
  /** The buyer's sign-in address, shown to the organizer of the event only; null without one. */
  buyerEmail: z.string().nullable(),
})
export type TicketOrderListItem = z.infer<typeof ticketOrderListItemSchema>

/** GET /api/businesses/{businessId}/tickets/events/{eventId}/orders/page */
export const ticketOrdersPageSchema = z.object({
  orders: z.array(ticketOrderListItemSchema),
  /** Orders matching the filters across all pages. */
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
})
export type TicketOrdersPage = z.infer<typeof ticketOrdersPageSchema>

/** Sales of one ticket type. Issued counts valid tickets; money is in cents, net after the fee. */
const eventTicketTypeFiguresSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  priceCents: z.number().int(),
  doorPriceCents: z.number().int().nullable(),
  isStudent: z.boolean(),
  active: z.boolean(),
  /** Seats on sale; null for no limit. */
  quantity: z.number().int().nullable(),
  issued: z.number().int(),
  checkedIn: z.number().int(),
  grossCents: z.number().int(),
  netCents: z.number().int(),
})

/** GET /api/businesses/{businessId}/tickets/events/{eventId}/summary */
export const eventTicketSummarySchema = z.object({
  event: z.object({
    id: z.string().uuid(),
    title: z.string(),
    startTime: z.string(),
    endTime: z.string(),
    isCanceled: z.boolean(),
  }),
  currency: z.string(),
  /** Has a paid ticket type or paid orders; false while the event only gives out free tickets. */
  sellsPaid: z.boolean(),
  /** Paid, free and refunded orders. */
  orderCount: z.number().int(),
  /** Valid tickets, free ones included. */
  issued: z.number().int(),
  freeIssued: z.number().int(),
  /** Seats across the types still on sale plus what the others gave out; null for no limit. */
  capacity: z.number().int().nullable(),
  checkedIn: z.number().int(),
  grossCents: z.number().int(),
  feeCents: z.number().int(),
  netCents: z.number().int(),
  refundedCents: z.number().int(),
  refundedOrders: z.number().int(),
  types: z.array(eventTicketTypeFiguresSchema),
})
export type EventTicketSummary = z.infer<typeof eventTicketSummarySchema>

/**
 * Where an event's ticket money stands: `none` nothing to pay out, `waiting` the event is not over
 * yet, `due` the payout is planned for `date`, `paid` it was paid on `date`, `failed` the last try
 * was refused and is retried, `review` Stripe still checks the account so nothing can be paid out.
 */
export const ticketPayoutStatusValues = [
  'none',
  'waiting',
  'due',
  'paid',
  'failed',
  'review',
] as const
export type TicketPayoutStatus = (typeof ticketPayoutStatusValues)[number]

export const ticketEarningsQuerySchema = z.object({
  /** Only the events this profile hosts; ignored while organization profiles are off. */
  hostProfileId: z.string().uuid().optional(),
})

/** Ticket money in cents. Gross is what buyers paid, net what is left after the Pulsify fee. */
const ticketEarningsFiguresSchema = z.object({
  ticketsSold: z.number().int(),
  freeTickets: z.number().int(),
  grossCents: z.number().int(),
  feeCents: z.number().int(),
  netCents: z.number().int(),
  refundedCents: z.number().int(),
  paidOutCents: z.number().int(),
  outstandingCents: z.number().int(),
})

export const ticketEarningsEventSchema = ticketEarningsFiguresSchema.extend({
  eventId: z.string().uuid(),
  hostProfileId: z.string().uuid().nullable(),
  title: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  coverImageUrl: z.string().nullable(),
  isCanceled: z.boolean(),
  /** Has a paid ticket type or paid orders; false while the event only gives out free tickets. */
  sellsPaid: z.boolean(),
  payout: z.object({
    status: z.enum(ticketPayoutStatusValues),
    /** When it was paid (`paid`) or is planned (`waiting`, `due`); null otherwise. */
    date: z.string().nullable(),
  }),
})
export type TicketEarningsEvent = z.infer<typeof ticketEarningsEventSchema>

/** GET /api/businesses/{businessId}/tickets/earnings: every event with tickets, newest first. */
export const ticketEarningsResponseSchema = z.object({
  currency: z.string(),
  totals: ticketEarningsFiguresSchema,
  /** The same money per profile; empty when one profile was asked for. */
  profiles: z.array(
    ticketEarningsFiguresSchema.extend({
      profileId: z.string().uuid().nullable(),
      name: z.string().nullable(),
    }),
  ),
  events: z.array(ticketEarningsEventSchema),
})
export type TicketEarnings = z.infer<typeof ticketEarningsResponseSchema>

export const ticketWebhookResponseSchema = z.object({
  received: z.boolean(),
  type: z.string().optional(),
})
